import { TIPO_INSPECAO } from "@/constants/TIPO_INSPECAO";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { usePermissao } from "@/hooks/usePermissao";
import { useTermoNotificacao } from "@/hooks/useTermoNotificacao";
import { ITermoNotificacao } from "@/interfaces/ITermoNotificacao";
import { ImpressaoTermoNotificacao } from "@/pages/termoNotificacao/_components/ImpressaoTermoNotificacao";
import { ModalCancelarTermoNotificacao } from "@/pages/termoNotificacao/_components/ModalCancelarTermoNotificacao";
import { formatMessageError } from "@/utils/formatMessageError";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MinusOutlined,
  PrinterOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { App, Button, List, Space, Tooltip, Typography } from "antd";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";

export function ItemListTermoNotificacao({
  item,
}: {
  item: ITermoNotificacao;
}) {
  const { isOnline } = useNetworkStatus();
  const termoNotificacaoRef = useRef<HTMLDivElement>(null);
  const {
    submitTermoNotificacaoApi,
    updateTermoNotificacao,
    removeTermoNotificacao,
  } = useTermoNotificacao();
  const { hasEnabledPermissao } = usePermissao();
  const { notification, message, modal } = App.useApp();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModalCancelar, setIsOpenModalCancelar] = useState(false);

  const handleSync = async () => {
    try {
      message.loading({
        content: "Sincronizando Termo de Notificação...",
        key: "loading",
        duration: 0,
      });
      setIsLoading(true);
      const result = await submitTermoNotificacaoApi(item);
      setIsLoading(false);
      message.destroy("loading");

      updateTermoNotificacao({
        ...item,
        id: result.id,
        numero: result.numero,
      });

      notification.success({
        message: "Termo de Notificação sincronizado com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao sincronizar o Termo de Notificação:", error);
      message.destroy("loading");
      setIsLoading(false);
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível sincronizar o Termo de Notificação",
        description: messageError,
      });
    }
  };

  const handleRemove = () => {
    modal.confirm({
      title: "Remover Termo de Notificação",
      content: "Tem certeza que deseja remover este Termo de Notificação?",
      okText: "Remover",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => {
        removeTermoNotificacao(item.offlineId!);
        notification.success({
          message: "Termo de Notificação removido com sucesso!",
        });
      },
    });
  };

  const redirectToEdit = () => {
    navigate(`/termos-notificacao/lista/editar/${item.offlineId}`);
  };

  const redirectToView = () => {
    navigate(`/termos-notificacao/visualizar/${item.id}`);
  };

  const handleRemoveLocal = () => {
    removeTermoNotificacao(item.offlineId!);
  };

  const handlePrint = useReactToPrint({
    contentRef: termoNotificacaoRef,
    documentTitle: "termo_notificacao",
    suppressErrors: true,
    preserveAfterPrint: true,
  });

  const handleOpenModalCancelar = () => {
    setIsOpenModalCancelar(true);
  };

  return (
    <>
      <List.Item
        extra={
          <Space>
            {!item.id && (
              <Button
                type="primary"
                size="small"
                ghost
                icon={<UploadOutlined />}
                onClick={handleSync}
                disabled={!isOnline || isLoading}
                loading={isLoading}
              >
                Sincronizar
              </Button>
            )}

            <Tooltip title="Imprimir" destroyOnHidden>
              <Button
                size="small"
                icon={<PrinterOutlined />}
                onClick={handlePrint}
              />
            </Tooltip>

            {!item.id && (
              <Tooltip title="Editar" destroyOnHidden>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={redirectToEdit}
                />
              </Tooltip>
            )}

            {item.id && (
              <Tooltip title="Visualizar" destroyOnHidden>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={redirectToView}
                  disabled={
                    !isOnline ||
                    !hasEnabledPermissao("TERMO_NOTIFICACAO_VISUALIZAR")
                  }
                />
              </Tooltip>
            )}

            {!item.id && (
              <Tooltip title="Remover" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleRemove}
                />
              </Tooltip>
            )}

            {item.id && (
              <Tooltip title="Cancelar" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={handleOpenModalCancelar}
                  disabled={
                    !isOnline ||
                    !hasEnabledPermissao("TERMO_NOTIFICACAO_CANCELAR")
                  }
                />
              </Tooltip>
            )}

            {item.id && (
              <Tooltip title="Remover (local)" destroyOnHidden>
                <Button
                  size="small"
                  danger
                  icon={<MinusOutlined />}
                  onClick={handleRemoveLocal}
                  disabled={!isOnline}
                />
              </Tooltip>
            )}
          </Space>
        }
      >
        <List.Item.Meta
          title={
            <Typography.Text
              strong
              style={{
                color: isOpenModalCancelar ? "#d7263d" : undefined,
                opacity: item.id ? 1 : 0.6,
              }}
            >
              {item.id
                ? `Termo Notificação ${item.numero}`
                : "Termo Notificação (Pendente)"}
            </Typography.Text>
          }
          description={
            <Typography.Text
              style={{
                color: isOpenModalCancelar ? "#d7263d" : "#8c8c8c",
                fontSize: 12,
              }}
            >
              Estabelecimento: {item.estabelecimento.numeroInspecao}-
              {TIPO_INSPECAO[item.estabelecimento.tipoInspecao]}-{item.estabelecimento.nome}
            </Typography.Text>
          }
        />
      </List.Item>

      <ModalCancelarTermoNotificacao
        termoNotificacaoId={item.id!}
        isOpen={isOpenModalCancelar}
        onClose={() => {
          setIsOpenModalCancelar(false);
        }}
        reloadItems={() => {}}
      />

      <div style={{ display: "none" }}>
        <ImpressaoTermoNotificacao
          marcadagua=""
          ref={termoNotificacaoRef}
          item={item}
        />
      </div>
    </>
  );
}
